import { useCallback } from "react";

import { createReference } from "@medplum/core";
import { ClinicalImpression, Practitioner } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import { showNotification } from "@mantine/notifications";

import {
  ClientNoteForm,
  ClientNoteStatus,
  ClientNoteTypeForm,
  updateClientNoteContent,
} from "@/clients/zod-schemas/client-note";
import { getClinicalImpressionData } from "@/fhir/resource-helpers/clinical-impression";

import { NoteEditorForm } from "../NoteEditorForm";

interface EditClientReminderNoteProps {
  clinicalImpression: ClinicalImpression;
  handleToggleEdit: () => void;
  setRefresh: (refresh: boolean | ((prev: boolean) => boolean)) => void;
}

export default function EditClientReminderNote({
  clinicalImpression,
  handleToggleEdit,
  setRefresh,
}: EditClientReminderNoteProps) {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner;
  const { summary } = getClinicalImpressionData(clinicalImpression);

  const handleEditNoteSubmit = useCallback(
    async (formValues: ClientNoteTypeForm) => {
      try {
        await updateClientNoteContent({
          medplum,
          clinicalImpression,
          form: formValues,
          practitionerReference: createReference(profile),
        });
        setRefresh((prev) => !prev);
        handleToggleEdit();
        showNotification({
          color: "green",
          message: "Reminder updated successfully",
        });
      } catch (err) {
        showNotification({
          color: "red",
          message: "Failed to update reminder",
          autoClose: false,
          styles: { description: { whiteSpace: "pre-line" } },
        });
      }
    },
    [medplum, clinicalImpression, profile, handleToggleEdit, setRefresh],
  );

  return (
    <NoteEditorForm<ClientNoteTypeForm>
      className="card"
      submitLabel="Update"
      schema={ClientNoteForm}
      onCancel={handleToggleEdit}
      onSubmit={handleEditNoteSubmit}
      initialValues={{
        noteStatus: clinicalImpression.status as ClientNoteStatus,
        noteText: summary || "",
      }}
      testIds={{
        text: "edit-reminder-note",
        cancel: "cancel-edit-reminder-note-button",
        submit: "update-reminder-note-button",
      }}
    />
  );
}
